/**
 * Call Tracker Utility
 * 
 * This utility helps prevent duplicate call notifications by tracking
 * call events, especially when sender and receiver are the same person.
 */

// Store for active calls and recently processed calls
const activeCallLinks = new Set();
const recentlyProcessedCalls = new Map();
const outgoingCalls = new Set();

/**
 * Track a new outgoing call to prevent echo notifications
 * @param {string} meetingLink - Meeting link of the call
 * @param {number} userId - User ID of the caller
 */
export const trackOutgoingCall = (meetingLink, userId) => {
  if (!meetingLink) return;
  
  console.log(`CallTracker: Tracking outgoing call with link ${meetingLink}`);
  outgoingCalls.add(meetingLink);
  
  // Also store as active call
  trackActiveCall(meetingLink, userId);
  
  // Auto-cleanup after 3 minutes
  setTimeout(() => {
    outgoingCalls.delete(meetingLink);
    console.log(`CallTracker: Removed tracking for outgoing call ${meetingLink}`);
  }, 3 * 60 * 1000);
};

/**
 * Track an active call
 * @param {string} meetingLink - Meeting link of the call
 * @param {number} userId - User ID participating in the call
 */
export const trackActiveCall = (meetingLink, userId) => {
  if (!meetingLink) return;
  
  console.log(`CallTracker: Tracking active call with link ${meetingLink}`);
  activeCallLinks.add(meetingLink);
  
  // Store as recently processed to prevent duplicate notifications
  recentlyProcessedCalls.set(meetingLink, {
    timestamp: Date.now(),
    userId
  });
  
  // Auto-cleanup after 3 minutes
  setTimeout(() => {
    activeCallLinks.delete(meetingLink);
    console.log(`CallTracker: Removed tracking for active call ${meetingLink}`);
  }, 3 * 60 * 1000);
};

/**
 * Check if an incoming call should be ignored (duplicate or self)
 * @param {Object} callData - Call data from server
 * @param {number} currentUserId - Current user's ID
 * @returns {boolean} - True if call should be ignored
 */
export const shouldIgnoreCall = (callData, currentUserId) => {
  if (!callData || !callData.meetingLink) return false;
  
  // Always ignore our own outgoing calls
  if (outgoingCalls.has(callData.meetingLink)) {
    console.log(`CallTracker: Ignoring notification for outgoing call ${callData.meetingLink}`);
    return true;
  }
  
  // Already in this call
  if (activeCallLinks.has(callData.meetingLink)) {
    console.log(`CallTracker: Ignoring notification for active call ${callData.meetingLink}`);
    return true;
  }
  
  // CRITICAL: Check for the specific case where receiver accepts the call
  // This happens when a 'receiver_ring' message is received immediately after accepting a call
  if (callData.type === 'receiver_ring') {
    // If this call was processed in the last 5 seconds, it's likely an echo after call acceptance
    const recentlyProcessed = Array.from(recentlyProcessedCalls.entries())
      .some(([link, data]) => {
        const isSameLink = link === callData.meetingLink;
        const isRecent = (Date.now() - data.timestamp) < 5000;
        return isSameLink && isRecent;
      });
    
    if (recentlyProcessed) {
      console.log(`CallTracker: Blocking receiver_ring message after recent call acceptance for ${callData.meetingLink}`);
      return true;
    }
  }
  
  // Check if we received this call notification recently (within 10 seconds)
  if (recentlyProcessedCalls.has(callData.meetingLink)) {
    const processedData = recentlyProcessedCalls.get(callData.meetingLink);
    
    // If processed within the last 10 seconds and by same user, ignore
    if (Date.now() - processedData.timestamp < 10000) {
      console.log(`CallTracker: Ignoring recently processed call ${callData.meetingLink}`);
      return true;
    }
  }
  
  // If the sender is the current user, ignore
  if (callData.sender_info && callData.sender_info.id === currentUserId) {
    console.log(`CallTracker: Ignoring call from self ${currentUserId}`);
    return true;
  }
  
  // If the user is already in a video call page
  if (window.location.pathname === '/videoCall') {
    console.log(`CallTracker: User is already in a video call, ignoring notification`);
    return true;
  }
  
  // Not a duplicate, track this call
  recentlyProcessedCalls.set(callData.meetingLink, {
    timestamp: Date.now(),
    userId: currentUserId
  });
  
  return false;
};

/**
 * Clean up call tracking when a call ends
 * @param {string} meetingLink - Meeting link of the call
 */
export const cleanupCall = (meetingLink) => {
  if (!meetingLink) return;
  
  console.log(`CallTracker: Cleaning up call ${meetingLink}`);
  activeCallLinks.delete(meetingLink);
  outgoingCalls.delete(meetingLink);
  recentlyProcessedCalls.delete(meetingLink);
};

/**
 * Reset all call tracking (useful for logout)
 */
export const resetAllCalls = () => {
  console.log(`CallTracker: Resetting all call tracking`);
  activeCallLinks.clear();
  outgoingCalls.clear();
  recentlyProcessedCalls.clear();
}; 